import * as React from 'react';
import {
  useReactTable,
  createColumnHelper,
  getCoreRowModel,
  flexRender
} from '@tanstack/react-table';

import classes from './EmployersReportsTable.module.scss';
import { FC } from 'react';
import Skeleton from '../../../Skeleton/Skeleton';

type EmployersReportTableType = {
  employer: string;
  calledFromQueue: string;
  served: string;
  redirected: string;
  missed: string;
  averageServiceTime: string;
  averageUnActiveTime: string;
  isSummary?: boolean;
};

const toInt = (value: string | null | undefined): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

const timeToSeconds = (value: string | null | undefined): number => {
  if (!value) return 0;
  const parts = String(value).split(':').map((p) => Number(p));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return 0;
  const [hh, mm, ss] = parts;
  return hh * 3600 + mm * 60 + ss;
};

const secondsToTime = (seconds: number): string => {
  const s = Math.max(0, Math.round(seconds));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
};

const pct = (num: number, den: number): string => {
  if (!den) return '0%';
  return `${Math.round((num / den) * 100)}%`;
};

const columnSizes = {
  employerSize: 220,
  calledFromQueueSize: 200,
  servedSize: 200,
  redirectedSize: 200,
  missedSize: 200,
  averageServiceTimeSize: 200,
  averageUnActiveTimeSize: 500,
  averageUnActiveTimeMinSize: 50
};

const {
  employerSize,
  calledFromQueueSize,
  servedSize,
  redirectedSize,
  missedSize,
  averageServiceTimeSize,
  averageUnActiveTimeSize,
  averageUnActiveTimeMinSize
} = columnSizes;

type EmployersReportsTableProps = {
  employeesReportData: EmployersReportTableType[];
  isLoadingData: boolean;
};

const EmployersReportTable: FC<EmployersReportsTableProps> = ({
  employeesReportData,
  isLoadingData
}) => {
  const columnHelper = createColumnHelper<EmployersReportTableType>();

  const categoriesColumns = [
    columnHelper.accessor('employer', {
      header: () => <div className={classes.headerContent}>Сотрудник</div>,
      cell: ({ row, getValue }) => (
        <div className={classes.colunmContent} style={row.original.isSummary ? { fontWeight: 700 } : undefined}>
          {getValue()}
        </div>
      ),
      size: employerSize
    }),
    columnHelper.accessor('calledFromQueue', {
      header: () => <div className={classes.headerContent}>Вызвано из очереди</div>,
      cell: ({ row, getValue }) => (
        <div className={classes.colunmContent} style={row.original.isSummary ? { fontWeight: 700 } : undefined}>
          {getValue()}
        </div>
      ),
      size: calledFromQueueSize
    }),
    columnHelper.accessor('served', {
      header: () => <div className={classes.headerContent}>Обслужено</div>,
      cell: ({ row, getValue }) => (
        <div className={classes.colunmContent} style={row.original.isSummary ? { fontWeight: 700 } : undefined}>
          {getValue()}
        </div>
      ),
      size: servedSize
    }),
    columnHelper.accessor('redirected', {
      header: () => <div className={classes.headerContent}>Перенаправлено</div>,
      cell: ({ row, getValue }) => (
        <div className={classes.colunmContent} style={row.original.isSummary ? { fontWeight: 700 } : undefined}>
          {getValue()}
        </div>
      ),
      size: redirectedSize
    }),
    columnHelper.accessor('missed', {
      header: () => <div className={classes.headerContent}>Не пришли</div>,
      cell: ({ row, getValue }) => (
        <div className={classes.colunmContent} style={row.original.isSummary ? { fontWeight: 700 } : undefined}>
          {getValue()}
        </div>
      ),
      size: missedSize
    }),
    columnHelper.accessor('averageServiceTime', {
      header: () => <div className={classes.headerContent}>Сред. время обслуживания</div>,
      cell: ({ row, getValue }) => (
        <div className={classes.colunmContent} style={row.original.isSummary ? { fontWeight: 700 } : undefined}>
          {getValue()}
        </div>
      ),
      size: averageServiceTimeSize
    }),
    columnHelper.accessor('averageUnActiveTime', {
      header: () => <div className={classes.headerContentRight}>Сред. время простоя</div>,
      cell: ({ row, getValue }) => (
        <div className={classes.columnContentRight} style={row.original.isSummary ? { fontWeight: 700 } : undefined}>
          {getValue()}
        </div>
      ),
      size: averageUnActiveTimeSize, //starting column size
      minSize: averageUnActiveTimeMinSize //enforced during column resizing
    })
  ];

  const columns = categoriesColumns;
  const [activeRow, setActiveRow] = React.useState(null);

  const handleClick = (id: string) => {
    setActiveRow(id);
  };

  const tableData = React.useMemo(() => {
    if (isLoadingData) return Array(5).fill({}) as EmployersReportTableType[];
    const rows = (employeesReportData ?? []) as EmployersReportTableType[];
    if (!rows.length) return rows;

    const totals = rows.reduce(
      (acc, r) => {
        acc.called += toInt(r.calledFromQueue);
        acc.served += toInt(r.served);
        acc.redirected += toInt(r.redirected);
        acc.missed += toInt(r.missed);
        acc.serviceTimeSumSec += timeToSeconds(r.averageServiceTime);
        acc.downTimeSumSec += timeToSeconds(r.averageUnActiveTime);
        acc.count += 1;
        return acc;
      },
      {
        called: 0,
        served: 0,
        redirected: 0,
        missed: 0,
        serviceTimeSumSec: 0,
        downTimeSumSec: 0,
        count: 0
      }
    );

    const summary: EmployersReportTableType = {
      employer: 'Итого',
      calledFromQueue: String(totals.called),
      served: `${totals.served} (${pct(totals.served, totals.called)})`,
      redirected: `${totals.redirected} (${pct(totals.redirected, totals.called)})`,
      missed: `${totals.missed} (${pct(totals.missed, totals.called)})`,
      averageServiceTime: secondsToTime(totals.count ? totals.serviceTimeSumSec / totals.count : 0),
      averageUnActiveTime: secondsToTime(totals.count ? totals.downTimeSumSec / totals.count : 0),
      isSummary: true
    };

    return [...rows, summary];
  }, [isLoadingData, employeesReportData]);
  const tableColumns = React.useMemo(
    () =>
      isLoadingData
        ? columns.map((column) => ({
            ...column,
            cell: () => <Skeleton />
          }))
        : columns,
    [isLoadingData, columns]
  );

  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    getCoreRowModel: getCoreRowModel()
  });
  return (
    <div className={classes.tableContainer}>
      <table className={classes.table}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{ width: `${header.getSize()}px` }}>
                    <div className={classes.thTitle}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      <div
                        {...{
                          onDoubleClick: () => header.column.resetSize(),
                          onMouseDown: header.getResizeHandler(),
                          className: `resizer ltr ${header.column.getIsResizing() ? 'isResizing' : ''}`
                        }}>
                        <div className={classes.colorResizer} />
                      </div>
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => handleClick(row.id)}
              className={activeRow === row.id ? classes.activeRow : ''}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default EmployersReportTable;
